import useIntersectionObserver from '@/hooks/shared/useIntersectionObserver';

import ArtDirectedImage from '@/components/shared/ui/ArtDirectedImage';

import ResponsiveSvgImage from './ResponsiveSvgImage';
import { ImageType } from './solutionItemTypes';

const SOLUTION_IMAGE_SIZES = '(min-width: 1136px) 1056px, calc(100vw - 40px)';

/** Fallback intrinsic widths, kept from when the data carried height alone. */
const DEFAULT_WIDTH = { desktop: 1104, mobile: 1005 } as const;

type SolutionImageProps = {
  alt: string;
  desktop: ImageType;
  language: 'en' | 'ko';
  mobile: ImageType;
};

type ResolvedSource = {
  src: string;
  width: number;
  height: number;
};

/**
 * The artwork for one breakpoint in the page's language, with the size that
 * belongs to that file: the two languages can be separate exports of
 * different proportions, and a size borrowed from the other one would
 * letterbox the drawing inside its box.
 */
function resolveSource(
  image: ImageType,
  language: 'en' | 'ko',
  fallbackWidth: number,
): ResolvedSource {
  const useEnglish = language === 'en' && image.src.en !== undefined;

  return {
    src: useEnglish ? (image.src.en ?? image.src.ko) : image.src.ko,
    width:
      (useEnglish ? (image.enWidth ?? image.width) : image.width) ??
      fallbackWidth,
    height: useEnglish ? (image.enHeight ?? image.height) : image.height,
  };
}

/**
 * ### SolutionImage
 *
 * The architecture drawing, art-directed: a wide diagram down to the large
 * breakpoint and a stacked one below it. Both are chosen by the browser
 * through `<picture>` rather than by a media-query hook, so the right
 * drawing is in the markup on first paint and neither one is fetched twice.
 *
 * Vector and raster take different routes because only one of them gains
 * anything from the image pipeline: an SVG is served as authored, while a
 * PNG or JPEG is resized and re-encoded per breakpoint.
 *
 * @returns The rendered diagram
 */
export default function SolutionImage({
  alt,
  desktop,
  language,
  mobile,
}: SolutionImageProps) {
  const desktopSource = resolveSource(desktop, language, DEFAULT_WIDTH.desktop);
  const mobileSource = resolveSource(mobile, language, DEFAULT_WIDTH.mobile);
  const usesResponsiveSvg =
    desktopSource.src.endsWith('.svg') && mobileSource.src.endsWith('.svg');
  const revealRef = useIntersectionObserver<HTMLDivElement>();

  return (
    // intersectionRevealStyles 는 globals.css 의 .intersection-reveal 과 동일한 정의라 클래스로 대체한다.
    <div ref={revealRef} className="intersection-reveal">
      {usesResponsiveSvg ? (
        <ResponsiveSvgImage
          alt={alt}
          desktopHeight={desktopSource.height}
          desktopSrc={desktopSource.src}
          desktopWidth={desktopSource.width}
          mobileHeight={mobileSource.height}
          mobileSrc={mobileSource.src}
          mobileWidth={mobileSource.width}
        />
      ) : (
        <ArtDirectedImage
          className="block w-full [&_img]:h-auto [&_img]:w-full"
          alt={alt}
          desktop={desktopSource}
          mobile={mobileSource}
          quality={86}
          sizes={SOLUTION_IMAGE_SIZES}
        />
      )}
    </div>
  );
}
